import { Box } from '@primer/react';
import { AccessSpace } from '@upright/common/iam/access/types';
import { CompanyType } from '@upright/common/model/input/companies/types';
import { MeasurableGroupType } from '@upright/common/model/input/measurable-group/types';
import { BouColor } from '@upright/ui/common/generic/global/bou-colors';
import { useIsClientOrTeam, useIsTeam } from '@upright/ui/common/generic/hooks';
import { friendlyCompanyType, friendlyGroupType, friendlyModelEntityType } from '@upright/ui/untidy/texts';
import { capitalize } from 'lodash';
import styled from 'styled-components';
import { CompanyProductSetQuality } from '../../../../../../../../common/internal/product-resolver/types';
import { isProductLike } from '../../../../../../../../common/model/input';
import { ModelEntityType } from '../../../../../../../../common/model/input/types';
import { ModelEntitySearchResponseItem } from '../../../../../../../../common/model/search/types';
import { friendlyProductSetQuality } from '../../../../../../../../common/model/texts/friendly-product-set-quality';
import { themeDEPRECATED } from '../../../../../../generic/global/styles';

interface Props {
  item: ModelEntitySearchResponseItem;
}

export function SpecialInformationBlock(props: Props) {
  const isCustomer = useIsClientOrTeam();
  const isTeam = useIsTeam();

  const entityTypeText = (
    <EntityTypeText
      entityType={props.item.type}
      groupType={props.item.groupType}
      companyType={props.item.companyType}
    />
  );
  const accessSpaceText = isCustomer ? <FundAccessSpaceText accessSpace={props.item.accessSpace} /> : null;
  const productInfoText = isTeam && isProductLike(props.item.type) ? <ProductInfoBlock itemId={props.item.id} /> : null;
  const companyInfoText =
    isTeam && props.item.type === ModelEntityType.COMPANY ? (
      <CompanyInfoBlock productSetQuality={props.item.fuzzyProductSetQuality as CompanyProductSetQuality | undefined} />
    ) : null;

  return (
    <SpecialInfoRoot
      sx={{
        color: 'text.secondary',
        display: 'flex',
        flexDirection: 'row',
        justifyContent: 'flex-end',
        textAlign: 'right',
        alignItems: 'center',
      }}
    >
      {entityTypeText}
      {accessSpaceText}
      {productInfoText}
      {companyInfoText}
    </SpecialInfoRoot>
  );
}

const ProductInfoBlock = (props: { itemId: string }) => {
  return <span>{props.itemId}</span>;
};

const CompanyInfoBlock = (props: { productSetQuality: CompanyProductSetQuality | undefined }) => {
  if (!props.productSetQuality) return null;
  return <span>{friendlyProductSetQuality(props.productSetQuality)}</span>;
};

function EntityTypeText(props: {
  entityType: ModelEntityType;
  groupType: MeasurableGroupType | undefined;
  companyType: CompanyType | undefined;
}) {
  switch (props.entityType) {
    case ModelEntityType.GROUP:
      if (props.groupType) {
        return <span>{capitalize(friendlyGroupType(props.groupType))}</span>;
      }
      break;
    case ModelEntityType.COMPANY:
      if (props.companyType) {
        return <span>{capitalize(friendlyCompanyType(props.companyType))}</span>;
      }
      break;
    case ModelEntityType.PRODUCT:
    case ModelEntityType.MIXIN:
    case ModelEntityType.PHENOMENON:
    case ModelEntityType.PORTFOLIO:
    default:
      break;
  }
  return <span>{friendlyModelEntityType(props.entityType)}</span>;
}

const FundAccessSpaceText = (props: { accessSpace: AccessSpace | undefined }) => {
  if (!props.accessSpace) return null;

  return <span>{getFriendlyFundUniverseName(props.accessSpace)}</span>;
};

const getFriendlyFundUniverseName = (accessSpace: AccessSpace | undefined) => {
  switch (accessSpace) {
    case AccessSpace.SHARED:
    case AccessSpace.PROMISED_PUBLIC:
    case AccessSpace.SEMIPRIVATE:
      return 'Upright database';
    case AccessSpace.PRIVATE:
      return 'User import';
    case undefined:
      return undefined;
  }
};

const SpecialInfoRoot = styled(Box)`
  > *:not(:last-child)::after {
    content: '·';
    margin-left: 4px;
    margin-right: 4px;
  }
  & > span {
    white-space: nowrap;
  }
  color: ${BouColor.PLATFORM_TEXT_GRAY_LIGHTER};
  ${themeDEPRECATED.monoFontNormal};
  margin-right: 12px;
`;
